/**
 * Every pure derivation the prototype performed inside `renderVals()` /
 * `ptVals()`, extracted as standalone functions.
 *
 * Line references throughout point at `design/Games Tonight Mobile.dc.html`,
 * which is the source of truth for behaviour (docs/PLAN.md). Nothing in this
 * file touches the DOM, the store, or the network: given the same arguments it
 * returns the same value, which is what makes the port testable.
 *
 * DEVIATION (P2-01): the prototype's derived objects carried inline closures
 * (`open`, `click`, `pick`, `add`). Those are omitted here — a selector that
 * mints new functions on every call breaks referential equality and defeats
 * memoization in React. The UI binds store actions itself; every *value* the
 * template read is preserved.
 */

import { fallGradient, fmt$, fmtD, fmtPk, initialsOf, MONTHS, norm, playersTxt, discountTxt, maxRungOf, ptFallGradient, revMeta, soloOnlyCats, togetherCats } from './format.ts';
import type {
  ArtCand,
  AssetMap,
  Filters,
  Game,
  GameId,
  ListEntry,
  Playtest,
  PtBrowseRow,
  PtCheckData,
  PtCheckResult,
  PtGroup,
  SharedTier,
  SortMode,
  Tier,
  TriState,
  User,
  VoteInput,
  VoteValue,
} from './types.ts';

// ---------------------------------------------------------------------------
// constants (prototype lines 1293, 1702, 1763, 1988, 2249, 2266, 2543)
// ---------------------------------------------------------------------------

export interface LvlMeta {
  /** the label — `MUST PLAY`, and the second half of every `S · MUST PLAY` line */
  t: string;
  /** the glyph the chips, buttons and row headers print: the upper-case letter */
  g: string;
  /** accent: text on an unselected control, fill on a selected one */
  c: string;
  /** soft fill — the row label's background wash */
  lab: string;
  /** rank weight; the Tonight average is taken over these */
  v: number;
}

/**
 * The four calls, best to worst (prototype line 1432). Every piece of copy and
 * chrome a call carries lives here, because the alternative is the same four
 * strings spelled out on Tonight, Interests, Detail, What's New and the move
 * sheet in two view trees.
 *
 * `v` is load-bearing beyond display: `agg()` averages it, so the numbers are
 * the model (S3 · A2 · B1 · C0) and not a presentation choice. The bottom rung
 * weighing 0 rather than a negative is why PLAY IT WITHOUT ME drags an average
 * down without cancelling somebody else's MUST PLAY — the binary board's
 * `eff = inN - outN` subtraction is gone with it.
 */
export const LVL: Record<VoteValue, LvlMeta> = {
  s: { t: 'MUST PLAY', g: 'S', c: 'var(--acc)', lab: 'var(--accSoft)', v: 3 },
  a: { t: 'WANT TO PLAY', g: 'A', c: 'var(--gt-text)', lab: 'var(--gt-raised)', v: 2 },
  b: { t: 'I COULD BE CONVINCED', g: 'B', c: 'var(--gt-text-5)', lab: 'var(--gt-field)', v: 1 },
  c: { t: 'PLAY IT WITHOUT ME', g: 'C', c: 'var(--acc2)', lab: 'var(--acc2Soft)', v: 0 },
};

/**
 * Display and storage order — the single source of truth for the call row
 * sequence, doing for Interests exactly what `TIERS` does for the GOTY ladder.
 * The board rows, the list-mode button strip, the move sheet acts and
 * `flattenIntRows` all enumerate this rather than the letters; adding a call
 * means editing this array, the `VoteValue` union and the `Record<VoteValue, …>`
 * tables, each of which TypeScript then flags.
 */
export const CALLS: readonly VoteValue[] = ['s', 'a', 'b', 'c'];

/**
 * Narrow a `data-gt-row` attribute — a bare `string`, since the DOM cannot carry
 * the union — back to a `VoteValue`. Anything that routes a drop by row key must
 * go through this rather than spelling the letters again: T-01 records what the
 * hand-written test cost on the GOTY board, and `dragEnd`'s binary
 * `'y' | 'n'` cast was the same shape one widening earlier — a drop onto a row
 * outside the cast read as a cancelled gesture rather than as an error.
 */
export function isCall(v: string): v is VoteValue {
  return (CALLS as readonly string[]).includes(v);
}

/**
 * Rank weight, better = higher. The prototype ran S=3…X=0; widening the ladder
 * to C-F moved the numbers but not the two properties anything could depend on:
 * every grade outranks `X`, and `S` is the maximum.
 */
export const TIERV: Record<Tier, number> = { S: 7, A: 6, B: 5, C: 4, D: 3, E: 2, F: 1, X: 0 };

/**
 * Display and storage order. `TIERS` is the single source of truth for the row
 * sequence — `gotyVM` maps it to build the board, `flattenTierRows` concatenates
 * in this order, and `SupabaseRepository` ranks `tierOrder` by it. Adding a tier
 * means editing this array, the `Tier` union, and the four `Record<Tier, …>`
 * tables below/in the selectors; TypeScript flags each one.
 */
export const TIERS: readonly Tier[] = ['S', 'A', 'B', 'C', 'D', 'E', 'F', 'X'];

/**
 * Narrow a `data-gt-row` attribute — a bare `string`, since the DOM cannot carry
 * the union — back to a `Tier`. Anything that routes a drop by row key must go
 * through this rather than spelling the letters again: the ladder widened to
 * S/A/B/C/D/E/F/X while the store's `dragEnd` still tested `'S' | 'A' | 'B' |
 * 'X'`, so every long-press drop onto C-F was silently discarded.
 */
export function isTier(v: string): v is Tier {
  return (TIERS as readonly string[]).includes(v);
}

/**
 * The TO-DO drop target's row key, in the same namespace as the tier row keys
 * and deliberately not a `Tier` — dropping there UNPLACES rather than placing
 * (`unplaceTier`, not `placeTierAt`). Desktop's rail carries this contract in
 * `zoneProps`, which is order-less and needs no key; mobile hit-tests
 * `[data-gt-row]`, so it needs a name for the same target.
 */
export const GOTY_TODO_ROW = 'todo';

/**
 * The Interests TO-DO panel's row key — the same contract as `GOTY_TODO_ROW`
 * one board over (DESIGN-CHANGELOG 2026-08-24). Dropping a capsule here CLEARS
 * the call (`voteSet(id, '')`) rather than setting one, which is why it is not
 * an `IntRowId`: `'w'` was the fifth ROW's key and that row is gone, while this
 * is a panel beside the four. Distinct from `'todo'` so a mobile hit-test can
 * never route an interests drop into `unplaceTier`.
 */
export const INT_TODO_ROW = 'int-todo';

/** `TN` — the toast/move-sheet tier labels (prototype lines 1380, 2516). */
export const TIER_NAMES: Record<Tier, string> = {
  S: 'S — GAME OF THE YEAR',
  A: 'A — CONTENDER',
  B: 'B — HONORABLE MENTION',
  C: 'C — SOLID',
  D: 'D — MIDDLING',
  E: 'E — ROUGH',
  F: 'F — DID NOT DELIVER',
  X: 'CUT',
};

/** The move sheet spells the cut row `✕ CUT` (prototype line 2516). */
export const TIER_NAMES_SHEET: Record<Tier, string> = { ...TIER_NAMES, X: '✕ CUT' };

/** Prototype line 1988 — hardcoded browse catalog (INVENTORY §8.7). */
export const PT_BROWSE: readonly PtBrowseRow[] = [
  { appid: 3837830, name: 'StarRupture', dev: 'Creepy Jar', open: true, meta: 'TESTING NOW 2.8K', tags: ['OPEN SIGNUPS', '1-4 CO-OP'] },
  { appid: 1818140, name: 'Den of Wolves', dev: '10 Chambers', open: true, meta: 'TESTING NOW 6.1K', tags: ['OPEN SIGNUPS', '1-4 CO-OP'] },
  { appid: 2820740, name: 'Prologue: Go Wayback', dev: 'PlayerUnknown Productions', open: true, meta: 'TESTING NOW 340', tags: ['OPEN SIGNUPS', 'SOLO'] },
  { appid: 1422450, name: 'Deadlock', dev: 'Valve', open: false, meta: 'TESTING NOW 41K · NO PUBLIC SIGNUP', tags: ['INVITE ONLY', '6V6'] },
];

export const SORT_OPTS: ReadonlyArray<{ v: SortMode; t: string }> = [
  { v: 'rel', t: 'SORT: RELEVANCE' },
  { v: 'peak', t: 'SORT: PEAK PLAYERS' },
  { v: 'rating', t: 'SORT: RATING' },
  { v: 'new', t: 'SORT: NEWEST' },
  { v: 'priceAsc', t: 'SORT: PRICE ↑' },
];

/**
 * Discover PAGE SIZE — the initial reveal and the LOAD MORE increment.
 *
 * This was a hard truncation: results were sorted, cut to 60, and the match line
 * reported the cut count, so anything past 60 was dropped while the screen
 * claimed "60 MATCHES". It is now a window (`discoverShown`), and the match line
 * reports the true total.
 */
export const DISCOVER_LIMIT = 60;

/**
 * The GOTY board's year — the `goty_entries.year` every board write is scoped
 * to, and the year `steam-sync op=goty` is asked about.
 *
 * One constant because the two must agree: asking Steam for a different year
 * than the rows are filed under would file this year's games under last year's
 * board. The prototype hardcoded 2026 in both the copy and the data.
 */
export const GOTY_YEAR = 2026;

/** Prototype line 1790 — the hardcoded "2026 Steam history". */
export const GOTY_STEAM_FILL_APPIDS: readonly number[] = [
  553850, 3241660, 3527290, 3164500, 2622380, 1962700, 2183900, 1030300,
];

export function defaultFilters(): Filters {
  return {
    p: 0,
    pr: [0, 60],
    st: { demo: 'any', ea: 'any', rel: 'any', unrel: 'any' },
    disc: 0,
    rs: 0,
    pt: 'any',
    own: [],
  };
}

/**
 * appid → the members of the caller's own crew who own it, by `User.k`
 * (migration 3046). ABSENT IS NOT `[]`: absent means nobody has asked about
 * that appid yet, `[]` means we asked and nobody owns it, and only the second
 * may hide a row. `passFlt` tests presence for that reason.
 */
export type OwnerMap = Readonly<Record<number, readonly string[]>>;

// ---------------------------------------------------------------------------
// search / filter pipeline (prototype 1572–1604, 1737–1760)
// ---------------------------------------------------------------------------

/**
 * Query terms: split on whitespace FIRST, then normalize each.
 *
 * The order is the whole point. `norm` strips whitespace along with every other
 * non-alphanumeric, so normalizing the query as one string collapses
 * `deep rock` into the single inseparable token `deeprock` — which is exactly
 * the contiguous-substring behaviour this replaces, and why `rock deep` used to
 * find nothing (docs/DISCOVERY.md §1.4 defect 3).
 */
export function terms(q: string): string[] {
  return q.split(/\s+/).map((t) => norm(t)).filter((t) => t !== '');
}

/**
 * The popularity scalar every ordering falls back on.
 *
 * One function because three call sites have to agree: relevance tie-breaking,
 * the NEWEST secondary key, and the default browse order.
 *
 * `ccu` (players now) and `pk` (all-time peak) are NOT coalesced — they are
 * different scales and interleaving them produces a meaningless order. A row
 * with a live sample is ranked by it; a row without one (anything unreleased,
 * and every row until DISCOVERY.md item 1a ships) falls back to the curated
 * peak. Rows in the two groups are therefore not strictly comparable, which is
 * the honest outcome: `sortArr` breaks that tie on `pk` for both.
 */
export function popOf(g: Pick<Game, 'ccu' | 'pk'>): number {
  return g.ccu > 0 ? g.ccu : g.pk || 0;
}

/**
 * Legacy quick-filter chips + free-text match (prototype line 1572).
 *
 * `chips` is always `[]` in the mobile build — no UI writes `state.chips`
 * (INVENTORY §5.1, PORTING-NOTES D-06). The chip branches are ported anyway
 * because `pass()` is the documented entry point and the desktop build used
 * them; dropping them would silently change the function's contract.
 *
 * The text match is token-AND: every term must appear somewhere across the
 * name, the developer or the tags, in any order. Terms may land in different
 * fields — that is intended, and is what makes `larian turnbased` work.
 */
export function pass(g: Game, chips: readonly string[] = [], q = '', now: Date = new Date()): boolean {
  const ts = terms(q);
  if (ts.length) {
    const hay = [norm(g.n), norm(g.dev), ...g.tags.map((t) => norm(t))];
    if (!ts.every((t) => hay.some((h) => h.includes(t)))) return false;
  }
  for (const c of chips) {
    if (c === 'recent') {
      if (g.up || !g.rd || g.rd === 'TBA') return false;
      const p = g.rd.split('-');
      const mo = (now.getFullYear() - Number(p[0])) * 12 + (now.getMonth() + 1 - Number(p[1]));
      if (mo > 18) return false;
    } else if (c === 'upcoming') {
      if (!g.up) return false;
    } else if (c === 'sale') {
      if (!(g.o && g.p && g.o > g.p)) return false;
    } else if (c === 'free') {
      if (g.p !== 0) return false;
    } else if (c === 'p2' || c === 'p4' || c === 'p8') {
      if (!(g.mx >= Number(c.slice(1)))) return false;
    } else if (c === 'u10' || c === 'u25') {
      if (g.up || g.p === null || g.p === undefined || !(g.p < Number(c.slice(1)))) return false;
    } else if (c.indexOf('tag:') === 0) {
      if (!g.tags.includes(c.slice(4))) return false;
    } else if (c.indexOf('plat:') === 0) {
      if ((g.plat || '').indexOf(c.slice(5)) < 0) return false;
    }
  }
  return true;
}

/**
 * Popularity is clamped strictly below this before being added to a relevance
 * score, so a popular game can NEVER outrank a better textual match — it only
 * ever breaks ties inside a tier. Enforced rather than assumed: the old code
 * relied on real peak CCU happening to be smaller than the tier gap, which is a
 * property of the current seed data, not of the function.
 */
const POP_CEIL = 1e7;

/**
 * Relevance score (prototype line 1587), generalized to multi-term queries.
 *
 * Tiers, highest first: exact name · name prefix · name contains the query
 * contiguously · every term somewhere in the name · some terms in the name.
 * Developer and tag matches are additive bonuses. The gaps are sized so no
 * combination of bonuses plus popularity can lift a row into the tier above —
 * worst case is 2e7 + 1e7 + POP_CEIL = 4e7, against a smallest tier gap of 5e7.
 */
export function score(g: Game, q: string): number {
  const ts = terms(q);
  const pop = Math.min(popOf(g), POP_CEIL - 1);
  if (!ts.length) return pop;

  const n = norm(g.n);
  const joined = ts.join('');
  let s: number;
  if (n === joined) s = 1e9;
  else if (n.indexOf(joined) === 0) s = 5e8;
  else if (n.includes(joined)) s = 2e8;
  else {
    // `pass` already guaranteed every term matched SOMEWHERE, so the remaining
    // signal is how much of the query landed on the name specifically.
    const inName = ts.filter((t) => n.includes(t)).length;
    s = inName === ts.length ? 1e8 : (5e7 * inName) / ts.length;
  }
  const dev = norm(g.dev);
  if (ts.every((t) => dev.includes(t))) s += 2e7;
  if (ts.every((t) => g.tags.some((x) => norm(x).includes(t)))) s += 1e7;
  return s + pop;
}

/** Sort key for `SORT: NEWEST` (prototype line 1595). */
export function rdKey(g: Game): string {
  return g.up ? '9999-99' : g.rd && g.rd !== 'TBA' ? g.rd : '0000-00';
}

/** Prototype line 1596. Returns a copy; the input array is untouched. */
export function sortArr(arr: readonly Game[], mode: SortMode, q = ''): Game[] {
  const c = arr.slice();
  if (mode === 'rel') c.sort((a, b) => score(b, q) - score(a, q));
  else if (mode === 'peak') c.sort((a, b) => popOf(b) - popOf(a));
  else if (mode === 'rating') c.sort((a, b) => (b.rp || 0) - (a.rp || 0));
  else if (mode === 'priceAsc') {
    const key = (g: Game) => (g.p === null || g.p === undefined || g.up ? 9e9 : g.p);
    c.sort((a, b) => key(a) - key(b));
  } else if (mode === 'new') {
    // The previous comparator was `rdKey(b) < rdKey(a) ? -1 : 1`, which never
    // returns 0 and so violates the contract: two games sharing a release month
    // compared inconsistently depending on argument order, and the engine was
    // free to order them arbitrarily. Popularity is the secondary key, which
    // makes the result both legal and stable.
    c.sort((a, b) => {
      const ka = rdKey(a);
      const kb = rdKey(b);
      if (ka !== kb) return ka < kb ? 1 : -1;
      return popOf(b) - popOf(a);
    });
  }
  return c;
}

/**
 * Which facts a row actually carries (docs/DISCOVERY.md decision 5).
 *
 * A filter must distinguish **absent** from **false**, and absent must never
 * exclude. A live-search row arrives from `toLiveGame` / `toCsGame` with a name,
 * a price and nothing else — `mx:0`, `tags:[]`, `rd:''`, all three release flags
 * hard-coded false because neither upstream reports them. Reading those zeros as
 * answers is what silently deleted every live result the moment any filter was
 * on: the one situation where live search is the entire point.
 *
 * The same rule pre-empts the identical problem for the thin shadow-pool rows of
 * Phase 2, which will know their categories but not their player count.
 */
export interface Known {
  players: boolean;
  tags: boolean;
  /** `up` / `ea` — the RELEASED, UPCOMING and EARLY ACCESS tri-states. */
  status: boolean;
  /**
   * The DEMO tri-state, scoped apart from `status` because its upstream is
   * one-sided. See `knownOf`.
   */
  demo: boolean;
  /** There is a price to reason about — the discount tri-state's precondition. */
  price: boolean;
  playtest: boolean;
  /**
   * NO `review` KEY, deliberately. Every other filter needs a separate "was
   * this fact answered" boolean; the review floor does not, because
   * `format.ts#maxRungOf` folds the gate into its own answer — it returns 9,
   * which passes every floor the panel offers, for a row nothing bounds.
   */
}

export function knownOf(g: Game): Known {
  return {
    // `catalog_games.max_players` is NOT NULL DEFAULT 1, so 0 is never a real
    // catalog value — it only ever means "nobody told us".
    players: g.mx > 0,
    tags: g.tags.length > 0,
    // Release state is trustworthy on a row that came with release data — OR on
    // a browse row whose `up`/`ea` GetItems actually answered (`Game.stOk`).
    // `!g.live` alone was written when every `live` row came from CheapShark or
    // live search, which report no release state at all; a browse row is `live`
    // and does report one, and reading it as unknown left three of the four
    // status filters inert across an entire tag browse.
    status: !g.live || g.stOk === true,
    // NOT folded into `status`, because GetItems is one-sided here: it names a
    // demo it knows of and never says "no demo", so `demo: false` on a browse
    // row is silence rather than an answer. Only a row with real release data
    // can answer the negative, and the tri-state needs the negative — HIDE DEMOS
    // acts on `!g.demo`. Absent never excludes (decision 5).
    demo: !g.live,
    // `p === null` is "no price on this row", which is every browse row: §6
    // keeps the volatile set (price, discount) off the cached browse page
    // deliberately, so `toBrowseGame` hard-codes `p: null, o: 0`. The discount
    // check reads `o > p` and both sentinels fail it, so an ungated DISCOUNTED
    // filter hid every Steam result rather than none of them — the one filter
    // that was over-applying while the rest under-applied.
    price: g.p !== null && g.p !== undefined,
    // A CheapShark row with no Steam appid (`cs<id>`) cannot be matched against
    // a tracked playtest at all.
    playtest: g.appid > 0,
  };
}

/**
 * Prototype line 1737. `pts` powers the `PLAYTEST` tri-state row.
 *
 * EVERY FILTER APPLIES ON EVERY SURFACE (2026-08-27, Marcus). Decision 6 used to
 * stop this function at the crew lens under `'lens'` scope, on the reasoning
 * that price and status describe a search rather than a list. The screens then
 * had to hide those sections, which made the one shared panel render five
 * different ways — the thing the merge of `# TAGS` into `+ FILTERS` was meant to
 * end. Every filter now narrows on whatever surface it was set on — and only
 * that one, because the state behind it is per screen (`store/filterKey.ts`).
 * This function is deliberately ignorant of both: it is handed a `Filters` and
 * asked whether one row survives it.
 *
 * The `scope` argument is gone with the branch it fed. `FilterScope` itself is
 * NOT: one distinction is still real, and it is not about which filters apply —
 * on Discover the picked TAGS are the question sent to Steam, off Discover they
 * are a local predicate over rows already in hand (`passTagLens`).
 */
export function passFlt(
  g: Game | null | undefined,
  f: Filters | null | undefined,
  pts: readonly Playtest[] = [],
  owners?: OwnerMap,
): boolean {
  if (!f || !g) return true;
  const known = knownOf(g);

  // SOLO IS THE LADDER'S BOTTOM RUNG, NOT A FLOOR OF ONE (2026-09-01, Marcus:
  // *"add the ability to filter by Solo games"*). Every other value of `f.p` is
  // a minimum — `4` means "fits at least four of us" — and `1` read that way
  // would be `mx >= 1`, true of every row that has a count at all: a segment
  // that lights up and narrows nothing. So `1` is the one EXACT value in the
  // row, and it asks the question the segment's label asks: is this a game for
  // one?
  //
  // Its two arms mirror the together-lens's below, sentinel for sentinel. A
  // resolved count answers outright (`mx === 1` is the same claim the tile
  // prints as `SOLO`); a row whose count is the 0 sentinel is judged on Steam's
  // categories, where the ONLY answer available is the negative — a named co-op
  // or multiplayer mode means this is not a game for one. A block that names
  // neither, or no block at all, is silence, and silence keeps the row
  // (decision 5).
  if (f.p === 1) {
    if (known.players) {
      if (g.mx !== 1) return false;
    } else if (togetherCats(g.pcats)) return false;
  } else if (f.p) {
    if (known.players && !(g.mx >= f.p)) return false;
    // The lens's POSITIVE-KNOWLEDGE arm, for rows whose seat COUNT is the 0
    // sentinel — every browse row, every pool mint. "Fits N of us" cannot be
    // verified without a number, but its strongest negative can be: a row whose
    // categories say Single-player and name no co-op or multiplayer mode does
    // not fit ANY together-lens, so it fails the lens at 2+ exactly as it would
    // at 8. `soloOnlyCats` is false on absence, so decision 5 holds: a row with
    // no categories block still passes, and only Steam's own positive claim
    // hides.
    if (f.p >= 2 && !known.players && soloOnlyCats(g.pcats)) return false;
  }

  const pr = f.pr || [0, 60];
  if (pr[0] > 0 || pr[1] < 60) {
    // A game we KNOW is upcoming has no settled price, so a price range still
    // excludes it. A row whose price is merely absent is unknown, and passes.
    if (known.status && g.up) return false;
    if (g.p !== null && g.p !== undefined) {
      if (g.p < pr[0]) return false;
      if (pr[1] < 60 && g.p > pr[1]) return false;
    }
  }
  const st = f.st;
  const chk = (mode: TriState | undefined, val: boolean) =>
    mode === 'only' ? !!val : mode === 'hide' ? !val : true;
  // Two gates, not one, because the demo flag and the release flags come off
  // different halves of the same upstream and only one of them can answer a
  // negative — `knownOf` has the argument.
  if (known.demo && !chk(st?.demo, g.demo)) return false;
  if (known.status) {
    if (!chk(st?.ea, g.ea)) return false;
    if (!chk(st?.rel, !g.up)) return false;
    if (!chk(st?.unrel, g.up)) return false;
  }
  if (f.disc && known.price) {
    // Known-gated on the PRICE, which is the field the check actually reads.
    // The rule is still "no old price means not discounted" — that stays a real
    // answer for any row carrying a price — but a row carrying NO price is not
    // making that claim, and treating its `o: 0, p: null` sentinels as one hid
    // every browse result behind this filter.
    const has = !!(g.o && g.p && g.o > g.p);
    if (!has) return false;
    if (f.disc > 1 && Math.round((1 - (g.p as number) / g.o) * 100) < f.disc) return false;
  }
  // The review floor, on STEAM'S RUNG rather than on `rp`. The rung is the only
  // review value that knows how many people are behind the percentage — Steam
  // folds the count in — so `rs >= 8` means "Very Positive or better" the way
  // Steam means it, and a 96% game with twelve reviews (rung 7, `Positive`)
  // correctly fails it. Filtering on the percentage would pass it.
  //
  // `maxRungOf` carries the known-gate itself: it answers 9 for a row nothing
  // bounds, so decision 5 holds without a separate `known.*` branch.
  if (f.rs && maxRungOf(g) < f.rs) return false;
  if (f.pt && f.pt !== 'any' && known.playtest) {
    const tracked = pts.some((e) => e.appid && e.appid === g.appid && !e.ended);
    if (f.pt === 'only' && !tracked) return false;
    if (f.pt === 'hide' && tracked) return false;
  }
  // OWNED BY — AND over the named members, and known-gated like everything
  // above it. `owners` is a CLIENT CACHE of migration 3046's answers, so its
  // three states are all reachable and all different: no map at all (a caller
  // that has no ownership data — every existing test, `LocalRepository`'s
  // catalog pass), no ENTRY for this appid (not asked yet, or a CheapShark row
  // with no appid to ask about), and an entry that is genuinely empty (asked,
  // nobody owns it). Only the last hides a row; the first two are unknown, and
  // unknown never excludes (DISCOVERY decision 5).
  //
  // That gate is why the count settles rather than jumps: a board renders
  // un-narrowed for the round trip and tightens when the answer lands, which is
  // the same shape `crewOwns` gives the Detail roster. The FEED does not go
  // through here at all — `discover_page` applies the identical AND server-side
  // (migration 3047) because only it can keep `total` honest.
  if (f.own && f.own.length > 0 && g.appid > 0) {
    const owned = owners?.[g.appid];
    if (owned && !f.own.every((k) => owned.includes(k))) return false;
  }
  return true;
}

/**
 * The exclude-tags predicate — `discover_page`'s `xtags` filter as executable
 * client code. `LocalRepository.fetchDiscoverPage` applies it so the dev
 * double and the contract tests agree with the server about what an exclude
 * means: a row KNOWN to carry an excluded editorial slug is dropped; an
 * untagged row passes, because excluding on data we do not have would drop
 * every unknown row (decision 5 — unknown never excludes).
 *
 * Editorial slugs only, on purpose. The server's exclude matches EITHER
 * vocabulary — editorial slug or resolved Steam tag id — but local rows carry
 * no Steam tag ids, so the second arm has no local data to run against:
 * absent, not asked, passes. The browse surface's id-based exclude lives in
 * `discoverVM`, over `Game.tids`, and the two never compare across
 * namespaces.
 */
export function passXTags(g: Game, xslugs: readonly string[]): boolean {
  if (xslugs.length === 0 || !knownOf(g).tags) return true;
  return !xslugs.some((t) => g.tags.includes(t));
}

/**
 * The picked tags as a LOCAL predicate — Tonight, Interests, GOTY, Playtests.
 *
 * Discover does not use this: there the include list IS the question Steam is
 * asked (`slices/browse.ts#browseKey`) and re-testing the answer against the
 * question is a no-op at best. Off Discover nobody asks Steam anything, so the
 * same picked tags have to narrow the rows already in hand or the picker would
 * be a control that visibly does nothing on four of the five screens it is now
 * offered on (2026-08-27, Marcus).
 *
 * `byName` is what makes it possible at all. Only browse rows carry `tids`;
 * a catalog row carries `stags` — the SAME Steam vocabulary, as names, from
 * `games.steam_tags` — so the vocabulary the picker already loaded resolves one
 * into the other and the comparison stays ids-to-ids. This is the documented
 * exception to `Game.stags` being display-only: the prohibition there is on
 * comparing Steam's words against the editorial `tags` list, and no such
 * comparison happens here.
 *
 * INCLUDES ARE AND, matching what Steam does with them and what the picker's
 * own `N SEARCH — STEAM MATCHES ALL` line promises. A row that can answer
 * neither way — no `tids`, no `stags` — passes: unknown never excludes
 * (DISCOVERY decision 5).
 */
export interface TagLens {
  inc: readonly number[];
  exc: readonly number[];
  byName: ReadonlyMap<string, number>;
}

export function passTagLens(g: Game, t: TagLens | null): boolean {
  if (!t) return true;
  let ids: number[];
  if (g.tids?.length) {
    ids = g.tids;
  } else if (g.stags?.length) {
    ids = [];
    for (const name of g.stags) {
      const id = t.byName.get(name.toLowerCase());
      if (id !== undefined) ids.push(id);
    }
    if (ids.length === 0) return true;
  } else {
    return true;
  }
  const set = new Set(ids);
  for (const id of t.inc) if (!set.has(id)) return false;
  for (const id of t.exc) if (set.has(id)) return false;
  return true;
}

/**
 * THE BOARD SEARCH (DESIGN-CHANGELOG 2026-08-28, "A search box on every board").
 *
 * A free-text term over rows the crew ALREADY HAS — the opposite job from
 * Discover's `q`, which is a question sent to Steam and can bring back rows the
 * crew has never seen. The design is explicit that the two never merge.
 *
 * WHICH FIELDS. The design says "name / developer / Steam tags", which is the
 * prototype's three: there, `games-data.js` puts Steam's tag names straight into
 * `g.tags`. Here those are two different fields and only one of them may be read
 * by a predicate: `tags` is the crew's editorial word list that `passFlt`
 * already compares by exact string, while `stags` is Steam's 446-word
 * vocabulary and is DISPLAY ONLY by an explicit contract (`lib/types.ts#stags`
 * — "nothing may read this in a filter"). So this reads `tags`, which is also
 * the list the board tiles actually render, and a term therefore matches what
 * the row shows you.
 *
 * Normalisation is `norm` — lowercase alphanumerics only — so `co-op`, `CO OP`
 * and `coop` are one term, and it is the SAME normaliser the rest of the app
 * matches names with.
 *
 * An empty or whitespace-only term passes everything: no query is not a filter.
 */
export function passQ(g: Game | null | undefined, q: string | null | undefined): boolean {
  const term = norm(q);
  if (!term) return true;
  if (!g) return true;
  if (norm(g.n).includes(term)) return true;
  if (norm(g.dev).includes(term)) return true;
  return g.tags.some((t) => norm(t).includes(term));
}

/**
 * The ONE predicate every board pool runs — `passFlt && passTagLens && passQ`,
 * in the design's words `passBoard`.
 *
 * It exists so the four board pools (Tonight, Interests, GOTY's potentials, the
 * round's nominate rail) cannot drift apart: before this they each spelled the
 * conjunction out, and the search would have had to be added to four places
 * that are only conventionally in step. The tag lens is folded in here for the
 * same reason — it was already the second half of that conjunction at every
 * one of those sites.
 *
 * Discover does NOT call this: its tags are the question asked of Steam rather
 * than a local predicate (`store/selectors.ts#scopeOf`), and its term is `q`.
 */
export function passBoard(
  g: Game | null | undefined,
  f: Filters | null | undefined,
  tl: TagLens | null,
  q: string | null | undefined,
  pts: readonly Playtest[] = [],
  owners?: OwnerMap,
): boolean {
  if (!g) return true;
  return passFlt(g, f, pts, owners) && passTagLens(g, tl) && passQ(g, q);
}

/**
 * Number badge on the `+ FILTERS · N` button (prototype line 1754).
 *
 * Unscoped for the same reason `passFlt` is: every filter applies on every
 * surface now, so every filter counts on every surface. TAGS are counted by the
 * caller (`bTags.length + xTags.length`) rather than here — they live in the
 * store, not in `Filters`.
 */
export function fltCount(f: Filters | null | undefined): number {
  if (!f) return 0;
  let n = 0;
  if (f.p) n++;
  const pr = f.pr || [0, 60];
  if (pr[0] > 0 || pr[1] < 60) n++;
  for (const k of ['demo', 'ea', 'rel', 'unrel'] as const) if (f.st[k] !== 'any') n++;
  if (f.disc) n++;
  if (f.rs) n++;
  if (f.pt !== 'any') n++;
  // ONE PER MEMBER, unlike every other line here and like the TAGS the caller
  // adds on top: each selected member is its own removable chip on the rail, so
  // the badge has to agree with the number of chips the user can see and ✕.
  n += f.own?.length ?? 0;
  return n;
}

/** Price-range label (prototype line 2278). */
export function priceLabel(pr: readonly [number, number]): string {
  const active = pr[0] > 0 || pr[1] < 60;
  if (!active) return 'ANY';
  if (pr[1] === 0) return 'FREE';
  if (pr[0] === 0) return 'UP TO $' + pr[1];
  if (pr[1] === 60) return '$' + pr[0] + '+';
  return '$' + pr[0] + '–$' + pr[1];
}

/** Range-slider snapping: within 2 of a multiple of 10 snaps to it (line 2300). */
export function snapPrice(v: number): number {
  const m = Math.round(v / 10) * 10;
  return m > 0 && m < 60 && Math.abs(v - m) <= 2 ? m : v;
}

// ---------------------------------------------------------------------------
// player-count chart (prototype 1605–1617)
// ---------------------------------------------------------------------------

export interface Bar {
  key: string;
  h: string;
  c: string;
  /**
   * The desktop per-bar tooltip (`data-tip`), e.g. `240K PLAYERS · 12D AGO`.
   *
   * Emitted here rather than reconstructed downstream. `selectors.d2.ts` used
   * to re-run the chart's PRNG to recover the pre-rounded ratios the `h`
   * percentages had thrown away — a whole file existing because the heights
   * were lossy. With real samples the number is simply known at the point the
   * bar is built, so it travels with it. Empty for a bucket with no samples:
   * there is nothing to say about a day nobody measured.
   */
  tip: string;
}

export interface BarsVM {
  has: boolean;
  bars: Bar[];
  foot: string;
  /** Days between the oldest sample and today, or 0 when the series is empty. */
  trackedDays: number;
  /** `true` while the window is younger than 90 days — drives `TRACKING <n>D`. */
  young: boolean;
}

/** One row of `game_ccu_samples`: a UTC day and that day's observed peak. */
export interface CcuSample {
  /** ms since epoch, as stored — NOT assumed to be midnight-aligned. */
  at: number;
  ccu: number;
}

/** How many bars `mkBars` emits. */
export const BAR_COUNT = 36;

/** The chart's window. The `90D AGO … TODAY` axis labels assert this number. */
export const CHART_DAYS = 90;

const DAY_MS = 86_400_000;

/**
 * Collapse raw samples to one point per UTC day, keeping each day's MAX.
 *
 * Two different reasons, and both are real data rather than defensive coding.
 * `upsert_ccu_sample` merges with `GREATEST` so a day already holds its peak —
 * but rows written before migration 2904 carry unbucketed `now()` stamps and
 * survive until the 120-day purge ages them out, so a single UTC day can hold
 * several rows. `refresh_pop_scores` re-buckets by the same rule; matching it
 * means the chart and the popularity score never disagree about what a day is.
 */
export function bucketByDay(series: readonly CcuSample[]): Map<number, number> {
  const out = new Map<number, number>();
  for (const s of series) {
    if (!Number.isFinite(s.at) || !Number.isFinite(s.ccu)) continue;
    const day = Math.floor(s.at / DAY_MS);
    const prev = out.get(day);
    // `>` not `>=`, and 0 is a legitimate observation: a game with nobody
    // playing measured zero, which is a floor bar rather than a missing day.
    out.set(day, prev === undefined || s.ccu > prev ? s.ccu : prev);
  }
  return out;
}

/**
 * The 90-day players chart, drawn from real samples.
 *
 * **This used to be a Lehmer PRNG seeded off the appid** (prototype line 1605),
 * scaled to the curated all-time peak and labelled `SAMPLE`. It is gone from
 * every production path: `game_ccu_samples` has held one row per appid per UTC
 * day since 2026-08-11, `SELECT` was granted to `authenticated` from the start,
 * and nothing ever asked for it. Closes P6 gap 12.9 / INVENTORY item 9.
 *
 * **Forward-only, by necessity.** Steam publishes no per-app CCU history —
 * `GetNumberOfCurrentPlayers` answers one instant, `ISteamChartsService` covers
 * the top 100, and the third parties that do hold history either forbid
 * scraping or are themselves unlicensed scrapes. So a game accrues bars from
 * its first sweep onward and a young chart must READ as young: `trackedDays`
 * and `young` exist so the caller can say `TRACKING 3D` instead of rendering
 * three bars in a 90-day frame and looking broken.
 *
 * **36 buckets of 2.5 days** keeps the design's bar geometry against a fixed
 * 90-day axis, so the `90D AGO` / `TODAY` labels stay true no matter how much
 * history exists. Each bucket is the max of its days; a bucket with no samples
 * is a gap (`h: '0%'`), which is why an all-zero day must NOT be a gap — a
 * measured zero and an unmeasured day are different claims.
 */
export function mkBars(series: readonly CcuSample[], now: number): BarsVM {
  const byDay = bucketByDay(series);
  if (byDay.size === 0) return { has: false, bars: [], foot: '', trackedDays: 0, young: true };

  const today = Math.floor(now / DAY_MS);
  const firstDay = today - CHART_DAYS + 1;
  const perBucket = CHART_DAYS / BAR_COUNT; // 2.5

  const buckets: (number | null)[] = [];
  const agoDays: number[] = [];
  for (let i = 0; i < BAR_COUNT; i++) {
    const lo = firstDay + Math.floor(i * perBucket);
    const hi = firstDay + Math.floor((i + 1) * perBucket); // exclusive
    let v: number | null = null;
    for (let d = lo; d < hi; d++) {
      const s = byDay.get(d);
      if (s !== undefined && (v === null || s > v)) v = s;
    }
    buckets.push(v);
    // Label the bucket by its most recent day, so the rightmost bar reads
    // TODAY rather than "2D AGO" for a bucket that includes today.
    agoDays.push(today - (hi - 1));
  }

  const seen = [...byDay.entries()].filter(([d]) => d >= firstDay && d <= today);
  if (seen.length === 0) {
    // Every sample we hold is older than the window — the game was tracked once
    // and has not been swept since. That is not "no data", and it is not a
    // chart either; the caller's empty state says which.
    return { has: false, bars: [], foot: '', trackedDays: 0, young: true };
  }

  const oldest = Math.min(...seen.map(([d]) => d));
  const trackedDays = today - oldest + 1;
  const windowMax = Math.max(...seen.map(([, v]) => v));
  const todayVal = byDay.get(today);

  // Scale off the window max so the tallest bar fills the box. A window whose
  // every sample is 0 would divide by zero; render it as a floor of flat bars
  // rather than NaN heights, because "nobody was playing" is a real answer.
  const scale = windowMax > 0 ? windowMax : 1;
  const lastIdx = buckets.reduce((acc, v, i) => (v !== null ? i : acc), -1);

  const foot =
    'PEAK ' +
    fmtPk(windowMax) +
    (todayVal !== undefined ? ' · TODAY ' + fmtPk(todayVal) : '') +
    (trackedDays < CHART_DAYS ? ' · TRACKING ' + trackedDays + 'D' : '');

  return {
    has: true,
    trackedDays,
    young: trackedDays < CHART_DAYS,
    bars: buckets.map((v, i) => ({
      key: 'b' + i,
      // A measured bucket never renders as nothing: floor it at 2% so a real
      // zero is visibly a bar and only a gap is empty.
      h: v === null ? '0%' : Math.max(2, Math.round((v / scale) * 100)) + '%',
      c: i === lastIdx ? 'var(--acc)' : '#242440',
      tip:
        v === null
          ? ''
          : fmtPk(v) + ' PLAYERS · ' + (agoDays[i] === 0 ? 'TODAY' : (agoDays[i] ?? 0) + 'D AGO'),
    })),
    foot,
  };
}

// ---------------------------------------------------------------------------
// the 12-month price sparkline (migration 3045, docs/DETAIL-BAND.md lane C)
// ---------------------------------------------------------------------------

/** One row of `game_price_samples`: a UTC day and the price observed on it. */
export interface PriceSample {
  /** ms since epoch for the sample's UTC DAY. */
  at: number;
  /** Dollars. `0` is a measurement — the game was free that day. */
  price: number;
}

/**
 * The price window. The design labels this band `PRICE · 12 MONTHS`, and that
 * label is an assertion about this number the same way the player chart's
 * `90D AGO … TODAY` axis asserts `CHART_DAYS`.
 */
export const PRICE_CHART_DAYS = 365;

export interface PriceBarsVM {
  has: boolean;
  bars: Bar[];
  foot: string;
  /** Days between the oldest sample in the window and today; 0 when empty. */
  trackedDays: number;
  /** `true` while the window is younger than a year — drives `TRACKING <n>D`. */
  young: boolean;
  /** `$4.94`, or `''` when no low is worth claiming. See `lowTxt` below. */
  lowTxt: string;
  /** `DEC 2025` — the month the low was observed. `''` whenever `lowTxt` is. */
  lowWhen: string;
}

/**
 * Collapse price samples into `BAR_COUNT` buckets across the trailing year.
 *
 * THREE THINGS DIFFER FROM `mkBars`, and each is a property of prices rather
 * than a style choice:
 *
 *  1. **A bucket keeps its MINIMUM, not its maximum.** `game_price_samples` is
 *     already one row per UTC day (the primary key enforces it), so unlike the
 *     CCU series there is nothing to collapse WITHIN a day — the reduction here
 *     is across the ~10 days a bucket spans. The lowest price in that span is
 *     the one worth showing, because the question a price history answers is
 *     "has this been cheaper?", and a mean or a last-value would hide exactly
 *     the sale the reader is looking for.
 *  2. **A lower bar is the interesting one**, the inverse of the player chart
 *     where taller is more. Heights still scale off the window maximum so the
 *     full-price bars top out the box and a discount visibly dips.
 *  3. **A zero is not a floor case.** A free game measures 0 every day, which
 *     would scale to a flat row of floor bars saying nothing; `has` is false
 *     for a series that never had a non-zero price, and the caller's empty
 *     state says the game is free rather than drawing a chart of $0.
 */
export function mkPriceBars(series: readonly PriceSample[], now: number): PriceBarsVM {
  const EMPTY: PriceBarsVM = {
    has: false,
    bars: [],
    foot: '',
    trackedDays: 0,
    young: true,
    lowTxt: '',
    lowWhen: '',
  };

  // One point per UTC day, keeping the day's LOWEST. The table's primary key
  // makes duplicates impossible today; this matches `upsert_pool_price_samples`'
  // own same-day rule (lowest wins) so the client and the writer can never
  // disagree about what a day cost.
  const byDay = new Map<number, number>();
  for (const s of series) {
    if (!Number.isFinite(s.at) || !Number.isFinite(s.price) || s.price < 0) continue;
    const day = Math.floor(s.at / DAY_MS);
    const prev = byDay.get(day);
    byDay.set(day, prev === undefined || s.price < prev ? s.price : prev);
  }
  if (byDay.size === 0) return EMPTY;

  const today = Math.floor(now / DAY_MS);
  const firstDay = today - PRICE_CHART_DAYS + 1;
  const perBucket = PRICE_CHART_DAYS / BAR_COUNT;

  const seen = [...byDay.entries()].filter(([d]) => d >= firstDay && d <= today);
  if (seen.length === 0) return EMPTY;

  const windowMax = Math.max(...seen.map(([, v]) => v));
  // Every sample is 0 — a free game. Not a chart; the caller says FREE.
  if (windowMax <= 0) return EMPTY;

  const buckets: (number | null)[] = [];
  const agoDays: number[] = [];
  for (let i = 0; i < BAR_COUNT; i++) {
    const lo = firstDay + Math.floor(i * perBucket);
    const hi = firstDay + Math.floor((i + 1) * perBucket); // exclusive
    let v: number | null = null;
    for (let d = lo; d < hi; d++) {
      const s = byDay.get(d);
      // `<` and a null check: the bucket's cheapest day wins.
      if (s !== undefined && (v === null || s < v)) v = s;
    }
    buckets.push(v);
    agoDays.push(today - (hi - 1));
  }

  const oldest = Math.min(...seen.map(([d]) => d));
  const trackedDays = today - oldest + 1;
  const lowDay = seen.reduce((acc, e) => (e[1] < acc[1] ? e : acc), seen[0] as [number, number]);
  const low = lowDay[1];
  const todayVal = byDay.get(today);
  // `reduce<number>` explicitly: with a `(number | null)[]` receiver TypeScript
  // will otherwise pick the overload where the accumulator widens to the element
  // type, and this index is USED as an index below rather than only compared to
  // one the way `mkBars`' identical line is.
  const lastIdx = buckets.reduce<number>((acc, v, i) => (v !== null ? i : acc), -1);

  /**
   * THE LOW CHIP'S GATE, and the honest answer to a claim we cannot yet make.
   *
   * The design prints `LOW $4.94 · DEC 2025` from seeded data. Ours can only
   * ever mean "the lowest price we have OBSERVED", and on the first night that
   * is simply today's price wearing a superlative — `LOW $29.99` beside
   * `NOW $29.99` says nothing and implies history we do not have.
   *
   * So the chip appears exactly when the price has actually MOVED below its
   * ceiling in the window: at that point "we have seen it cheaper, and here is
   * when" is a real, useful and bounded claim. A game whose price has never
   * changed gets no chip rather than a chip restating its price, and the
   * footer's `TRACKING <n>D` scopes how far back "ever" reaches either way.
   *
   * This is the rule migration 3045's header commits to — our own samples or
   * nothing — expressed as a render gate rather than as a wait for a year.
   */
  const hasLow = low < windowMax;

  const foot =
    (todayVal !== undefined ? 'NOW ' + money(todayVal) : 'LAST ' + money(buckets[lastIdx] ?? low)) +
    (hasLow ? ' · LOW ' + money(low) : '') +
    (trackedDays < PRICE_CHART_DAYS ? ' · TRACKING ' + trackedDays + 'D' : '');

  return {
    has: true,
    trackedDays,
    young: trackedDays < PRICE_CHART_DAYS,
    lowTxt: hasLow ? money(low) : '',
    lowWhen: hasLow ? monthOf(lowDay[0] * DAY_MS) : '',
    bars: buckets.map((v, i) => ({
      key: 'p' + i,
      // Floor at 2% so a measured $0 day inside a priced year is visibly a bar
      // and only an unsampled bucket is empty — `bucketByDay`'s rule, restated
      // for the reason its comment gives.
      h: v === null ? '0%' : Math.max(2, Math.round((v / windowMax) * 100)) + '%',
      c: i === lastIdx ? 'var(--acc)' : '#242440',
      tip:
        v === null
          ? ''
          : money(v) + ' · ' + (agoDays[i] === 0 ? 'TODAY' : (agoDays[i] ?? 0) + 'D AGO'),
    })),
    foot,
  };
}

/** `$9.99` / `FREE`. Prices are dollars throughout (`steam_facts.price`). */
function money(n: number): string {
  return n === 0 ? 'FREE' : '$' + n.toFixed(2);
}

/** `DEC 2025` from a ms timestamp, in UTC — the samples are UTC-day keyed. */
function monthOf(ms: number): string {
  const d = new Date(ms);
  return (MONTHS[d.getUTCMonth()] ?? '') + ' ' + d.getUTCFullYear();
}

// ---------------------------------------------------------------------------
// art candidate ladder (prototype 1474–1495)
// ---------------------------------------------------------------------------

const STEAM_CDN = 'https://cdn.cloudflare.steamstatic.com/steam/apps/';

/**
 * The ordered list of art URLs to try for one game. The UI walks it with
 * `Image()` probes exactly as `artTry` did; keeping the ladder pure means the
 * ordering is testable and the network part is the UI's problem.
 */
export function artCands(g: Game, kind: 'p' | 'w', assets: AssetMap = {}): ArtCand[] {
  const b = assets[String(g.appid)] ?? { lib: '', hdr: '' };
  const d = assets[String(g.dAid)] ?? { lib: '', hdr: '' };
  const artPortrait = /library_capsule|library_600x900/.test(g.art || '');
  const leg = (a: number, f: string) => (a ? STEAM_CDN + a + '/' + f : '');
  const out: ArtCand[] = [];
  const push = (u: string, m: 'cover' | 'contain') => {
    if (u) out.push({ u, m });
  };
  // A FETCHED asset outranks a GUESSED one, always.
  //
  // The legacy `/steam/apps/<appid>/library_600x900.jpg` shape used to lead this
  // ladder. It still resolves for older apps, and 404s for newer ones: verified
  // against appid 4704690 (MECCHA CHAMELEON), where all three legacy shapes
  // (`library_600x900`, `header`, `capsule_616x353`) are 404 and the only art
  // Steam publishes lives at a CONTENT-HASHED path
  // (`steam/apps/4704690/<sha>/library_capsule.jpg`) that cannot be derived from
  // the appid — it is only obtainable from a GetItems assets response.
  //
  // `useArt` walks this list and stops at the first `onload`, so a leading guess
  // was never a correctness bug; it was a wasted round trip per tile on every
  // modern game, and a permanently blank tile whenever the assets fetch had not
  // happened (which for GOTY-only rows was always — see `revalidateVisible`).
  // `b.*` comes from Steam's own asset manifest, so when we have it, it wins.
  if (kind === 'p') {
    push(b.lib, 'cover');
    push(leg(g.appid, 'library_600x900.jpg'), 'cover');
    if (artPortrait) push(g.art, 'cover');
    push(d.lib, 'cover');
    push(leg(g.dAid, 'library_600x900.jpg'), 'cover');
    push(b.hdr, 'contain');
    push(leg(g.appid, 'header.jpg'), 'contain');
    if (!artPortrait) push(g.art, 'contain');
    push(g.thumb !== g.art ? g.thumb : '', 'contain');
    push(leg(g.appid, 'library_hero.jpg'), 'cover');
    push(d.hdr, 'contain');
    push(leg(g.dAid, 'header.jpg'), 'contain');
  } else {
    push(b.hdr, 'cover');
    push(leg(g.appid, 'header.jpg'), 'cover');
    push(g.art, artPortrait ? 'contain' : 'cover');
    push(g.thumb !== g.art ? g.thumb : '', 'cover');
    push(leg(g.appid, 'library_hero.jpg'), 'cover');
    push(d.hdr, 'cover');
    push(leg(g.dAid, 'header.jpg'), 'cover');
  }
  return out;
}

// ---------------------------------------------------------------------------
// tile view-model (prototype `tile()`, 1536–1571)
// ---------------------------------------------------------------------------

export interface RibbonVM {
  ribShow: boolean;
  ribTxt: string;
  ribBg: string;
  ribTop: string;
  ribLeft: string;
  ribW: string;
  ribUnder: boolean;
  ribUnderTxt: string;
  ribUnderBg: string;
}

/** Corner-ribbon rules: UPCOMING ⊻ EARLY ACCESS, DEMO rides on top (line 1544). */
export function ribbonOf(g: Pick<Game, 'up' | 'ea' | 'demo'>): RibbonVM {
  const DEMO = { t: 'DEMO', bg: 'var(--acc)', top: '26px', left: '-48px', w: '170px' };
  let rib: { t: string; bg: string; top: string; left: string; w: string } | null = null;
  let under: { t: string; bg: string } | null = null;
  if (g.up) {
    if (g.demo) {
      rib = DEMO;
      under = { t: 'UPCOMING', bg: 'var(--acc2)' };
    } else rib = { t: 'UPCOMING', bg: 'var(--acc2)', top: '30px', left: '-44px', w: '180px' };
  } else if (g.ea) {
    if (g.demo) {
      rib = DEMO;
      under = { t: 'EARLY ACCESS', bg: 'var(--gt-info)' };
    } else rib = { t: 'EARLY ACCESS', bg: 'var(--gt-info)', top: '34px', left: '-42px', w: '190px' };
  } else if (g.demo) rib = DEMO;
  return {
    ribShow: !!rib,
    ribTxt: rib ? rib.t : '',
    ribBg: rib ? rib.bg : '',
    ribTop: rib ? rib.top : '0',
    ribLeft: rib ? rib.left : '0',
    ribW: rib ? rib.w : '0',
    ribUnder: !!under,
    ribUnderTxt: under ? under.t : '',
    ribUnderBg: under ? under.bg : '',
  };
}

export const TILE_SHEEN =
  'linear-gradient(225deg, rgba(255,255,255,.075) 0%, rgba(255,255,255,.05) 9%, rgba(255,255,255,.015) 16%, transparent 26%)';

export interface TileVM extends RibbonVM {
  key: string;
  id: GameId;
  name: string;
  dev: string;
  upcoming: boolean;
  ea: boolean;
  demo: boolean;
  hasDemoBtn: boolean;
  demoUrl: string;
  sheen: string;
  ini: string;
  fall: string;
  /** ordered art candidates; the UI probes them (see `artCands`) */
  coverCands: ArtCand[];
  hdrCands: ArtCand[];
  disc: string;
  hasDisc: boolean;
  priceTxt: string;
  oldTxt: string;
  hasOld: boolean;
  playersTxt: string;
  dateTxt: string;
  revTxt: string;
  revClr: string;
  /**
   * `revTxt` split at the space, for the tiles.
   *
   * A tile's review line is one `nowrap` + `ellipsis` span at 8.5px, and
   * Steam's rungs are long — `OVERWHELMINGLY POSITIVE` where the old ladder
   * said `OVERWHELMING`. Truncating one string eats the RIGHT end, which is the
   * percentage: the measurement disappears and the adjective survives. Rendered
   * as two spans, the label truncates and the number is pinned.
   */
  revLbl: string;
  revPct: string;
  peakTxt: string;
  inList: boolean;
  /** On the signed-in member's backlog (docs/ADD-IT.md) — yours alone. */
  inBacklog: boolean;
  /**
   * The tile's add control (docs/ADD-IT.md, decision 3): `+` on neither list,
   * `✓` on either. The FILL says which — accent fill = on the party list, the
   * shared fact everyone sees (unchanged); an accent OUTLINE over the tile's
   * dark = on your backlog only; on both, the filled form. The tip names the
   * state. Every state opens the ADD IT chooser — a `✓` no longer toasts
   * `ALREADY ON THE LIST`, because the second destination is still reachable.
   */
  addG: string;
  addBd: string;
  addClr: string;
  addBg: string;
  addTip: string;
  /** `Game.why`, server-authored. Empty on every non-feed row. */
  whyTxt: string;
  hasWhy: boolean;
}

export interface TileCtx {
  listIds: ReadonlySet<GameId>;
  /** The signed-in member's backlog ids; absent = nobody signed in / not asked. */
  backlogIds?: ReadonlySet<GameId>;
  assets: AssetMap;
}

/** `TileVM.addTip` — the four states of the Discover `+` (docs/ADD-IT.md). */
export function addTipOf(inList: boolean, inBacklog: boolean): string {
  if (inList && inBacklog) return 'ON BOTH LISTS';
  if (inList) return "ON TONIGHT'S LIST";
  if (inBacklog) return 'ON YOUR BACKLOG';
  return 'ADD TO GAMES TONIGHT? OR MY BACKLOG';
}

/** Prototype `tile()` minus the resolved art and the closures (see P2-01). */
export function tileVM(g: Game, ctx: TileCtx): TileVM {
  const rev = revMeta(g);
  const inL = ctx.listIds.has(g.id);
  const inB = !!ctx.backlogIds && ctx.backlogIds.has(g.id);
  const disc = discountTxt(g);
  return {
    key: '' + g.id,
    id: g.id,
    name: g.n,
    dev: g.dev,
    upcoming: g.up,
    ea: !!g.ea,
    demo: !!g.demo,
    hasDemoBtn: !!g.dAid,
    demoUrl: 'https://store.steampowered.com/app/' + (g.dAid || 0),
    ...ribbonOf(g),
    sheen: TILE_SHEEN,
    ini: initialsOf(g.n),
    fall: fallGradient(g.id),
    coverCands: artCands(g, 'p', ctx.assets),
    hdrCands: artCands(g, 'w', ctx.assets),
    disc,
    hasDisc: !!disc,
    priceTxt: fmt$(g),
    oldTxt: g.o ? '$' + g.o.toFixed(2) : '',
    hasOld: !!(g.o && g.p && g.o > g.p),
    playersTxt: playersTxt(g),
    dateTxt: fmtD(g),
    revTxt: rev.t,
    revClr: rev.c,
    revLbl: rev.l,
    revPct: rev.pct,
    peakTxt: fmtPk(g.pk),
    inList: inL,
    inBacklog: inB,
    addG: inL || inB ? '✓' : '+',
    addBd: inL || inB ? 'var(--acc)' : 'var(--gt-text)',
    addClr: inL ? 'var(--gt-ink)' : inB ? 'var(--acc)' : 'var(--gt-text)',
    addBg: inL ? 'var(--acc)' : 'rgba(5,5,8,.5)',
    addTip: addTipOf(inL, inB),
    // SERVER-AUTHORED AND RENDERED VERBATIM. `discover_page` decides this — the
    // client cannot recompute it without the taste profile and the ownership
    // join, which is exactly the data it is not allowed to hold. Empty on every
    // row that did not come from the feed, so the tile treats it as optional
    // chrome rather than a field with a default.
    whyTxt: g.why ?? '',
    hasWhy: !!g.why,
  };
}

/**
 * Where `id` sits in a member's hand-ordered array, with `1e9` for "not in it".
 *
 * Shared by BOTH boards that read a personal ordering, which is why it lives
 * above them rather than inside the interests section it was written for: MY
 * INTERESTS orders its own rows by it, and Games Tonight filtered to a single
 * member orders that member's tiers by the same array (3059). One definition,
 * so the two surfaces cannot disagree about where an unranked game goes.
 *
 * `1e9` rather than `Infinity` or `-1`: unknown ids sort LAST, and `Array#sort`
 * is stable, so they keep the order they arrived in instead of being shuffled
 * among themselves.
 */
const orderIndex = (ord: readonly GameId[], id: GameId) => {
  const i = ord.indexOf(id);
  return i < 0 ? 1e9 : i;
};

// ---------------------------------------------------------------------------
// Games Tonight? aggregation (prototype 2799–2827)
// ---------------------------------------------------------------------------

export interface Pair {
  e: ListEntry;
  g: Game;
}

export interface AggRow {
  x: Pair;
  /** mean call weight over `votedN`; `0` when nobody called */
  avg: number;
  /** how many of the filtered players called `c` — the `C ×n` chip */
  outN: number;
  /** how many of the filtered players called at all */
  votedN: number;
  /** `''` = NEEDS CALLS, i.e. below quorum */
  tier: SharedTier;
}

/** `['C','B','A','S'][Math.round(avg)]` — the weight ladder read backwards. */
const AVG_ROWS: readonly Exclude<SharedTier, ''>[] = ['C', 'B', 'A', 'S'];

/**
 * The row math (prototype line 2801): a game's row is the group's rounded
 * average call (S3 · A2 · B1 · C0) over the **filtered** players who called it,
 * and it holds in NEEDS CALLS until at least half of them have.
 *
 * Two details are the model rather than the expression, and must survive any
 * rewrite. `Math.round` is half-up, so an average of exactly 2.5 lands on `S`
 * and not on `A`. And the quorum floor is `max(1, …)` because `ceil(0/2)` is 0:
 * without it an empty selection would clear quorum with zero calls and file
 * every game under `C` (avg 0), which is the loudest possible way to say
 * nothing.
 *
 * The `?? ''` on the row lookup is `noUncheckedIndexedAccess` bookkeeping, not
 * a branch: `avg` is bounded by the weight table, so the index is always 0-3.
 */
export function agg(entries: readonly Pair[], selUsers: readonly User[]): AggRow[] {
  const quorum = Math.max(1, Math.ceil(selUsers.length / 2));
  return entries.map((x) => {
    const calls = selUsers
      .map((u) => x.e.votes[u.k])
      .filter((v): v is VoteValue => !!v && isCall(v));
    const sum = calls.reduce((s, v) => s + LVL[v].v, 0);
    const avg = calls.length ? sum / calls.length : 0;
    const outN = calls.filter((v) => v === 'c').length;
    const tier: SharedTier = calls.length >= quorum ? AVG_ROWS[Math.round(avg)] ?? '' : '';
    return { x, avg, outN, votedN: calls.length, tier };
  });
}

/** `agg`'s `AVG_ROWS` ladder in the lowercase alphabet `LVL` is keyed by. */
const VERDICT_ROWS: readonly VoteValue[] = ['c', 'b', 'a', 's'];

export interface Verdict {
  /** The badge letter, or `—` when nobody has called it. */
  big: string;
  /** `MUST PLAY` … or `NO CALLS YET`. */
  sub: string;
  /** The badge's background: a call colour, or the neutral border when none. */
  bg: string;
  /** How many crew members have called it — the `N OF M CALLED` numerator. */
  calledN: number;
}

/**
 * The detail panel's group verdict (prototype line 3174): the same rounded
 * S3·A2·B1·C0 average `agg` computes, over the members who have actually called.
 *
 * **It deliberately has no quorum, and `agg` forty lines above deliberately
 * does.** The two answer different questions and the difference is load-bearing.
 * `agg` decides what the crew has agreed to play tonight, so it holds a row in
 * NEEDS CALLS until at least half of them have spoken — without that floor one
 * `S` files a game above one three people actually want. This badge only reports
 * what has been said so far, and `calledTxt` (`2 OF 6 CALLED`) discloses the
 * thinness immediately beside it, so withholding the letter here would hide
 * information the caption is already qualifying.
 *
 * Merging them is the tempting simplification and it fails visibly: in a
 * six-person crew a game two people called `S` would read `— / NO CALLS YET` on
 * the panel header and on the phone's tab badge, where the design reads
 * `S / MUST PLAY`. `logic.test.ts` asserts both behaviours in a single `it` so
 * the difference cannot be quietly re-merged.
 */
export function detailVerdict(
  votes: Readonly<Record<string, VoteValue | undefined>> | undefined,
  users: readonly User[],
): Verdict {
  const calls = users.map((u) => votes?.[u.k]).filter((v): v is VoteValue => !!v && isCall(v));
  const avg = calls.length ? calls.reduce((s, v) => s + LVL[v].v, 0) / calls.length : 0;
  // Same shape as the prototype's `const L = LVL[key]` … `L ? L.g : '—'`: an
  // out-of-range index degrades to the honest em dash rather than to a letter.
  const key = calls.length ? VERDICT_ROWS[Math.round(avg)] : undefined;
  const L = key ? LVL[key] : null;
  return L
    ? { big: L.g, sub: L.t, bg: L.c, calledN: calls.length }
    : { big: '—', sub: 'NO CALLS YET', bg: 'var(--gt-border-2)', calledN: calls.length };
}

export const SHARED_ROW_KEYS: readonly Exclude<SharedTier, ''>[] = ['S', 'A', 'B', 'C'];

export interface SharedRowMeta {
  big: string;
  fs: string;
  sub: string;
  clr: string;
  lab: string;
}

/**
 * Row labels (line 2810). No `show` flag and no selection argument any more:
 * the rows are the four calls, so they mean the same thing whoever is filtered
 * in, and all four are always rendered. The binary board's labels were
 * player-count ranges (`ALL 6`, `3-4`) that only existed because `selN` decided
 * where the row boundaries fell.
 */
export function sharedRowMeta(): Record<Exclude<SharedTier, ''>, SharedRowMeta> {
  return {
    S: { big: 'S', fs: '14px', sub: LVL.s.t, clr: LVL.s.c, lab: LVL.s.lab },
    A: { big: 'A', fs: '14px', sub: LVL.a.t, clr: LVL.a.c, lab: LVL.a.lab },
    B: { big: 'B', fs: '14px', sub: LVL.b.t, clr: LVL.b.c, lab: LVL.b.lab },
    C: { big: 'C', fs: '14px', sub: LVL.c.t, clr: LVL.c.c, lab: LVL.c.lab },
  };
}

/**
 * Games inside one shared row, ordered by `avg` desc then `votedN` desc
 * (line 2818) — UNLESS the board is looking at exactly one member, in which
 * case `ord` is that member's own MY INTERESTS ordering and it wins outright.
 *
 * THE DEFAULT SORT DEGENERATES AT n=1, which is the whole reason `ord` exists
 * (Marcus, 2026-09-03: *"if I filter to just one person I can see their actual
 * personal board … the game order in each tier matches the order that they
 * actually set"*). With one member selected every game in a row was called the
 * same letter by the same person, so `avg` is a constant across the row and
 * `votedN` is 1 for all of it; both comparators return 0 and the stable sort
 * hands back the order the CREW happened to add the games in. The row was
 * labelled as one member's and ordered by nobody's.
 *
 * `ord` REPLACES the comparator rather than breaking its ties. A member's
 * ranking is the finer-grained statement — they placed `S` #1 above `S` #4 by
 * hand — and the average it would be tie-breaking is constant anyway. Games
 * they have not ranked sort last through `orderIndex`, stably, so they keep
 * list order among themselves.
 *
 * Pass `null`/`undefined` for every other case: all of the crew, several of
 * them, or one member whose ordering has not been read yet. An unknown order
 * must fall back to the average sort rather than to `orderIndex`'s all-`1e9`
 * flat answer, which would silently reorder nothing and look identical to a
 * successful read.
 */
export function sharedRowGames(
  rows: readonly AggRow[],
  t: Exclude<SharedTier, ''>,
  ord?: readonly GameId[] | null,
): AggRow[] {
  const inRow = rows.filter((a) => a.tier === t);
  return ord
    ? inRow.sort((a, b) => orderIndex(ord, a.x.e.id) - orderIndex(ord, b.x.e.id))
    : inRow.sort((a, b) => b.avg - a.avg || b.votedN - a.votedN);
}

/**
 * The NEEDS CALLS row, in the same two orders as `sharedRowGames` above.
 *
 * It is a separate function only because the rows it draws from are the ones
 * `agg` left WITHOUT a tier, and because its default is not the average sort:
 * an uncalled game has no average to sort by (`avg` is 0 for all of them), so
 * the crew-wide default is simply list order, exactly as it was. What it shares
 * with the rows above is the part that matters — filtered to one member it
 * reads in that member's order, which is what MY INTERESTS does with its own
 * `? NO CALL YET` bucket.
 */
export function sharedWaitGames(rows: readonly AggRow[], ord?: readonly GameId[] | null): AggRow[] {
  const wait = rows.filter((a) => !a.tier);
  return ord ? wait.sort((a, b) => orderIndex(ord, a.x.e.id) - orderIndex(ord, b.x.e.id)) : wait;
}

/**
 * `WHO'S PLAYING` toggle (prototype line 2342): first tap selects only that
 * user; selecting everyone collapses back to ALL.
 */
export function tfToggle(cur: readonly string[], k: string, totalUsers: number): string[] {
  let nf: string[];
  if (!cur.length) nf = [k];
  else if (cur.includes(k)) nf = cur.filter((x) => x !== k);
  else nf = [...cur, k];
  if (nf.length === totalUsers) nf = [];
  return nf;
}

// ---------------------------------------------------------------------------
// interests ordering (prototype 1522, 1972, 2094–2110)
// ---------------------------------------------------------------------------

/** A call row, or `''` for the `? NO CALL YET` row the board ends with. */
export type IntRowKey = VoteValue | '';

/**
 * The Interests board's row sequence, and the order `intOrder` flattens in.
 * Derived from `CALLS` rather than spelled out, so the five row keys exist in
 * exactly one place — `intRows`, `flattenIntRows`, the two order helpers and
 * both boards read this (T-01: the GOTY ladder widened at six hand-written
 * sites and one of them was missed).
 */
export const INT_ROW_KEYS: readonly IntRowKey[] = [...CALLS, ''];

/**
 * The ids in one Interests row, ordered by `intOrder` with unknown ids last
 * (prototype line 2094). `Array#sort` is stable, so unknowns keep list order.
 */
export function intRowIds(
  list: readonly ListEntry[],
  meKey: string,
  intOrder: readonly GameId[],
  v: IntRowKey,
): GameId[] {
  const ids = list
    .filter((e) => {
      const mv = e.votes[meKey];
      // The no-call row takes anything that is not a recognised call, not just
      // an absent key: a letter this build does not know (a pre-tier `y` that
      // slipped past the normalizer) must still land somewhere the owner can
      // see and re-call it, rather than vanishing off the board.
      return v ? mv === v : !mv || !isCall(mv);
    })
    .map((e) => e.id);
  return ids.sort((a, b) => orderIndex(intOrder, a) - orderIndex(intOrder, b));
}

export type IntRows = Record<IntRowKey, GameId[]>;

/**
 * Build an `IntRows` by asking `pick` for each row — the interests twin of
 * `tierRowsFrom`, and the only place the five row keys are enumerated.
 */
function intRowsFrom(pick: (v: IntRowKey) => GameId[]): IntRows {
  return Object.fromEntries(INT_ROW_KEYS.map((v) => [v, pick(v)])) as IntRows;
}

export function intRows(list: readonly ListEntry[], meKey: string, intOrder: readonly GameId[]): IntRows {
  return intRowsFrom((v) => intRowIds(list, meKey, intOrder, v));
}

/** Flatten s → a → b → c → '' back into the single `intOrder` array. */
export function flattenIntRows(rows: IntRows): GameId[] {
  return INT_ROW_KEYS.flatMap((v) => rows[v]);
}

/**
 * `voteSet` ordering (prototype line 1972): rebuild from the current list ids,
 * append anything unknown, then move the voted id to the very end.
 */
export function intOrderAfterVoteSet(
  intOrder: readonly GameId[],
  listIds: readonly GameId[],
  id: GameId,
): GameId[] {
  let ord = intOrder.filter((x) => listIds.includes(x));
  for (const x of listIds) if (ord.indexOf(x) < 0) ord = [...ord, x];
  return [...ord.filter((x) => x !== id), id];
}

/** `setIntAt` ordering (prototype line 1522): splice into the target row. */
export function intOrderAfterDrop(rows: IntRows, id: GameId, v: IntRowKey, idx: number): GameId[] {
  const next = intRowsFrom((k) => rows[k].filter((x) => x !== id));
  next[v].splice(Math.min(idx, next[v].length), 0, id);
  return flattenIntRows(next);
}

/**
 * The MY INTERESTS TO-DO queue (DESIGN-CHANGELOG 2026-08-24): the games I have
 * not called, **oldest first**, with previously-skipped ones pushed to the back
 * in skip order.
 *
 * This is a THIRD ordering rule, and the difference from its two neighbours is
 * the whole point:
 *   * `gotyQueue` orders by the potentials array — insertion order, because a
 *     GOTY potential is something you chose to add;
 *   * `interestsVM`'s list mode sorts NEWEST first, because that is a feed;
 *   * this one is oldest first, because it is a chore list. The game that has
 *     waited longest for your call is the one to answer next.
 *
 * `uncalled` is `intRowIds`' `''` rule, not `!votes[me]`: a letter this build
 * does not recognise is not a call, and its game must still reach the panel
 * where the owner can re-call it, exactly as it used to reach the `?` row.
 *
 * The caller passes the entries it considers visible (the lens-filtered set),
 * so the filter decision stays in one place — the view model — rather than
 * being re-litigated here.
 */
export function intQueue(
  entries: readonly ListEntry[],
  meKey: string,
  skips: readonly GameId[],
): GameId[] {
  const uncalled = entries
    .filter((e) => {
      const mv = e.votes[meKey];
      return !mv || !isCall(mv);
    })
    .slice()
    .sort((a, b) => a.at - b.at);
  const kept = uncalled.filter((e) => !skips.includes(e.id)).map((e) => e.id);
  const skipped = uncalled
    .filter((e) => skips.includes(e.id))
    .map((e) => e.id)
    .sort((a, b) => skips.indexOf(a) - skips.indexOf(b));
  return [...kept, ...skipped];
}

/**
 * Swap with a neighbour inside one row. Returns `null` at a boundary, which is
 * the prototype's `ALREADY FIRST` / `ALREADY LAST` toast (line 2100).
 */
export function intOrderAfterNudge(rows: IntRows, id: GameId, v: IntRowKey, dir: 1 | -1): GameId[] | null {
  const next = intRowsFrom((k) => [...rows[k]]);
  const row = next[v];
  const i = row.indexOf(id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= row.length) return null;
  row.splice(i, 1);
  row.splice(j, 0, id);
  return flattenIntRows(next);
}

// ---------------------------------------------------------------------------
// GOTY ordering (prototype 1369, 1705–1735, 1821–1835, 2404)
// ---------------------------------------------------------------------------

export type TierRows = Record<Tier, GameId[]>;

/** Prototype line 1821. */
export function tierRowIds(
  potentials: readonly GameId[],
  tiers: Readonly<Record<string, Tier>>,
  tierOrder: readonly GameId[],
  t: Tier,
): GameId[] {
  const ids = potentials.filter((id) => tiers[String(id)] === t);
  return ids.sort((a, b) => orderIndex(tierOrder, a) - orderIndex(tierOrder, b));
}

/**
 * Build a `TierRows` by asking `pick` for each row. The one place row keys are
 * enumerated — everything below goes through it, so the ladder's width lives in
 * `TIERS` alone and no function here has to be touched to add a tier.
 */
function tierRowsFrom(pick: (t: Tier) => GameId[]): TierRows {
  return Object.fromEntries(TIERS.map((t) => [t, pick(t)])) as TierRows;
}

export function tierRows(
  potentials: readonly GameId[],
  tiers: Readonly<Record<string, Tier>>,
  tierOrder: readonly GameId[],
): TierRows {
  return tierRowsFrom((t) => tierRowIds(potentials, tiers, tierOrder, t));
}

export function flattenTierRows(rows: TierRows): GameId[] {
  return TIERS.flatMap((t) => rows[t]);
}

/** `placeTier` ordering (prototype line 1711): move to the end of the flat list. */
export function tierOrderAfterPlace(tierOrder: readonly GameId[], id: GameId): GameId[] {
  return [...tierOrder.filter((x) => x !== id), id];
}

/** `placeTierAt` ordering (prototype line 1372). */
export function tierOrderAfterDrop(rows: TierRows, id: GameId, t: Tier, idx: number): GameId[] {
  const next = tierRowsFrom((k) => rows[k].filter((x) => x !== id));
  next[t].splice(Math.min(idx, next[t].length), 0, id);
  return flattenTierRows(next);
}

/** Prototype line 1827. `null` at a boundary. */
export function tierOrderAfterNudge(rows: TierRows, id: GameId, t: Tier, dir: 1 | -1): GameId[] | null {
  const next = tierRowsFrom((k) => [...rows[k]]);
  const row = next[t];
  const i = row.indexOf(id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= row.length) return null;
  row.splice(i, 1);
  row.splice(j, 0, id);
  return flattenTierRows(next);
}

/** `skipTodoId` / `unplaceTier`: move to the back of the skip queue (lines 1719, 1731). */
export function skipsAfterSkip(skips: readonly GameId[], id: GameId): GameId[] {
  return [...skips.filter((x) => x !== id), id];
}

/**
 * The TO-DO queue (prototype line 2404): unplaced potentials in insertion
 * order, with skipped ids pushed to the back **in skip order**.
 */
export function gotyQueue(
  potentials: readonly GameId[],
  tiers: Readonly<Record<string, Tier>>,
  skips: readonly GameId[],
  isVisible: (id: GameId) => boolean,
): GameId[] {
  const unplaced = potentials.filter((id) => isVisible(id) && tiers[String(id)] === undefined);
  const kept = unplaced.filter((id) => !skips.includes(id));
  const skipped = unplaced
    .filter((id) => skips.includes(id))
    .sort((a, b) => skips.indexOf(a) - skips.indexOf(b));
  return [...kept, ...skipped];
}

// ---------------------------------------------------------------------------
// what's new + attribution (prototype 1837, 2446)
// ---------------------------------------------------------------------------

/**
 * The What's New queue (prototype line 1837): entries somebody else added since
 * my last review that I have not voted on and have not dismissed.
 */
export function newForMe(
  list: readonly ListEntry[],
  me: Pick<User, 'k' | 'lastRev' | 'seen'> | null,
  gById: (id: GameId) => Game | null,
): Pair[] {
  if (!me) return [];
  const rev = me.lastRev || 0;
  const seen = new Set<GameId>(me.seen || []);
  const out: Pair[] = [];
  for (const e of list
    .filter((e) => e.by !== me.k && e.at > rev && !e.votes[me.k] && !seen.has(e.id))
    .sort((a, b) => b.at - a.at)) {
    const g = gById(e.id);
    if (g) out.push({ e, g });
  }
  return out;
}

export interface Stats {
  a: number;
  v: number;
  c: number;
}

/** `ADDED N · VOTES N · COMMENTS N` (prototype line 2446). */
export function stats(list: readonly ListEntry[], k: string): Stats {
  const a = list.filter((e) => e.by === k).length;
  let v = 0;
  let cN = 0;
  for (const e of list) {
    if (e.votes[k]) v++;
    cN += e.comments.filter((c) => c.by === k).length;
  }
  return { a, v, c: cN };
}

// ---------------------------------------------------------------------------
// playtests (prototype 1995–1996, 2036–2101)
// ---------------------------------------------------------------------------

/** Prototype line 1995. */
export function ptGroupOf(e: Playtest, now: number = Date.now()): PtGroup {
  if (e.ended) return 'ended';
  if (e.wStart && e.wEnd && e.wStart <= now && now <= e.wEnd) return 'live';
  if (e.wStart && e.wStart > now) return 'up';
  if (e.wEnd && e.wEnd < now) return 'ended';
  return 'tba';
}

export interface PtArtVM {
  /**
   * The ordered ladder for the 460×N header slot, for `useArt` to walk. NOT a
   * resolved background — see below for why a playtest cannot have one.
   */
  cands: ArtCand[];
  fall: string;
  ini: string;
}

/**
 * The art ladder for one playtest row — `artCands(g, 'w')` for the playtests
 * surface, and it exists for the same reason.
 *
 * This used to be a single GUESS applied straight to a CSS background:
 * `artUrl || /steam/apps/<appid>/header.jpg`, with `hasArt` true whenever that
 * string was non-empty. Both halves fail on exactly the games this screen is
 * for. A playtest is by definition an UNRELEASED game, so its appid is recent,
 * and the legacy CDN shapes 404 for anything recent — the fact already recorded
 * in `artCands` (appid 4704690) and in `screens/Goty.tsx`. Measured 2026-08-27,
 * `cdn.cloudflare.steamstatic.com/steam/apps/<appid>/header.jpg` is 404 for
 * HAEX (3524720), Scramble Knights Online (2379350) and Astromine (1833210),
 * all three of which DO have art — only at the content-hashed
 * `store_item_assets/steam/apps/<appid>/<sha>/header.jpg` path, which cannot be
 * derived from the appid and is obtainable only from a GetItems response.
 *
 * `artUrl` does not save it: `SupabaseRepository#ptDoTrack` writes no `art_url`
 * at all, so every manually tracked playtest has an empty one, and only the
 * auto-track trigger fills it (from `v_games`, when the game is known there).
 * That split is the "SOMETIMES the thumbnail does not populate" this replaces.
 *
 * So the answer is the assets map — the same `/api/steam/items` record every
 * other tile surface already fetches — with the guesses kept below it as the
 * path that still works for older appids. `hasArt`/`artBg` are gone from the
 * VM on purpose: a guessed URL is not evidence of art, and claiming it was is
 * what made a 404 render as a permanently blank tile instead of the initials.
 *
 * Order is fetched → stored → guessed. It diverges from `artCands`'s `'w'`
 * rungs, which put the legacy guess above `g.art`, because `artUrl` here is
 * never a guess: it reached the row from GetItems by way of `v_games`.
 */
export function ptArtCands(e: Pick<Playtest, 'artUrl' | 'appid'>, assets: AssetMap = {}): ArtCand[] {
  const b = assets[String(e.appid)] ?? { lib: '', hdr: '' };
  // A stored portrait capsule in a 460-wide slot is letterboxed, not cropped —
  // same test `artCands` makes.
  const portrait = /library_capsule|library_600x900/.test(e.artUrl || '');
  const out: ArtCand[] = [];
  const push = (u: string, m: 'cover' | 'contain') => {
    if (u) out.push({ u, m });
  };
  push(b.hdr, 'cover');
  push(e.artUrl, portrait ? 'contain' : 'cover');
  push(e.appid ? STEAM_CDN + e.appid + '/header.jpg' : '', 'cover');
  push(b.lib, 'contain');
  return out;
}

/** Prototype line 1996. */
export function ptArt(
  e: Pick<Playtest, 'artUrl' | 'appid' | 'hue' | 'name'>,
  assets: AssetMap = {},
): PtArtVM {
  return {
    cands: ptArtCands(e, assets),
    fall: ptFallGradient(e.hue),
    ini: initialsOf(e.name),
  };
}

export interface PtBuckets {
  int: User[];
  req: User[];
  inn: User[];
}

const stOf = (e: Playtest, k: string) => (e.st[k] ? e.st[k].s : '');

/**
 * INTERESTED / ⋯ REQUESTED / ✓ HAS ACCESS (prototype line 2044). Note that a
 * member who has a *status* drops out of the interest bucket.
 */
export function ptBuckets(e: Playtest, users: readonly User[]): PtBuckets {
  return {
    int: users.filter((u) => e.int[u.k] && !stOf(e, u.k)),
    req: users.filter((u) => stOf(e, u.k) === 'req'),
    inn: users.filter((u) => stOf(e, u.k) === 'in'),
  };
}

export interface PtWinBox {
  wLbl: string;
  wLblClr: string;
  wLive: boolean;
  d1: string;
  hasD2: boolean;
  d2: string;
  wFoot: string;
}

const ptDays = (ms: number) => Math.max(1, Math.round(Math.abs(ms) / 864e5));

/** Prototype line 2049. `ptFmt` is imported lazily to keep this module pure. */
export function ptWinBox(e: Playtest, now: number, fmt: (ms: number) => string): PtWinBox {
  const g = ptGroupOf(e, now);
  if (g === 'live')
    return { wLbl: 'LIVE NOW', wLblClr: 'var(--acc)', wLive: true, d1: fmt(e.wStart), hasD2: true, d2: fmt(e.wEnd), wFoot: ptDays(e.wEnd - now) + ' DAYS LEFT' };
  if (g === 'up')
    return { wLbl: 'UPCOMING', wLblClr: 'var(--gt-text-3)', wLive: false, d1: fmt(e.wStart), hasD2: true, d2: fmt(e.wEnd), wFoot: 'STARTS IN ' + ptDays(e.wStart - now) + 'D' };
  if (g === 'ended')
    return { wLbl: 'ENDED', wLblClr: 'var(--gt-muted)', wLive: false, d1: e.wEnd ? fmt(e.wEnd) : '—', hasD2: false, d2: '', wFoot: 'ARCHIVED' };
  return {
    wLbl: 'NO DATES',
    wLblClr: 'var(--gt-text-5)',
    wLive: false,
    d1: e.status === 'open' ? 'OPEN' : 'TBA',
    hasD2: false,
    d2: '',
    wFoot: e.foot || (e.status === 'open' ? 'WATCHING FOR WAVES' : 'NO SIGNUP PAGE'),
  };
}

export interface PtPassCtx {
  q: string;
  chips: readonly string[];
  meKey: string;
  flt: Filters;
  gByAppid: (appid: number) => Game | null;
  pts: readonly Playtest[];
  /** Picked tags, resolved against this row's catalog twin. Null when none. */
  tags: TagLens | null;
  /**
   * Crew ownership for the twin, same map `passFlt` takes. Optional so the
   * pure-`logic` tests can build a context without one; absent means every row
   * is ownership-UNKNOWN and OWNED BY narrows nothing, which is the safe
   * direction.
   */
  owners?: OwnerMap;
}

/** Prototype line 2079 — the playtest list filter. */
export function ptPass(e: Playtest, ctx: PtPassCtx): boolean {
  const cg = e.appid ? ctx.gByAppid(e.appid) : null;
  if (cg) {
    // The full filter set, like every other surface now. A playtest whose
    // catalog twin is known is judged on that twin — price, status, tags and
    // all — and the `else` branch below still answers the crew lens alone,
    // because a playtest with no twin has nothing else to be judged on.
    if (!passFlt(cg, ctx.flt, ctx.pts, ctx.owners)) return false;
    if (!passTagLens(cg, ctx.tags)) return false;
  } else if (ctx.flt.p === 1) {
    // SOLO, on the same split `passFlt` draws: an exact 1 rather than a floor
    // of one. `party.mx >= 1` would be a no-op on every row that states a party
    // size and would hide the ones that do not — backwards on both halves.
    if (e.party.mx > 1) return false;
  } else if (ctx.flt.p && !(e.party.mx >= ctx.flt.p)) return false;
  const ts = terms(ctx.q);
  if (ts.length) {
    const hay = [norm(e.name), norm(e.dev)];
    if (!ts.every((t) => hay.some((h) => h.includes(t)))) return false;
  }
  const my = ctx.meKey ? stOf(e, ctx.meKey) : '';
  for (const c of ctx.chips) {
    if (c === 'canjoin') {
      if (!(e.status === 'open' && my !== 'in' && !e.ended)) return false;
    } else if (c === 'coop') {
      if (!(e.party.mx >= 2)) return false;
    } else if (c === 'off') {
      if (e.kind !== 'off') return false;
    } else if (c === 'fin') {
      if (!Object.values(e.st).some((x) => x.s === 'in')) return false;
    }
  }
  return true;
}

/** The squad dot row; `party.vs` adds an opposing squad (prototype line 2056). */
export function ptSquads(e: Playtest): Array<{ key: string; bg: string; bd: string; ml: string }> {
  const n = Math.min(e.party.mx || 1, 6);
  const out: Array<{ key: string; bg: string; bd: string; ml: string }> = [];
  for (let i = 0; i < n; i++) out.push({ key: 'g' + i, bg: 'var(--acc)', bd: 'var(--gt-ink)', ml: '0' });
  if (e.party.vs) for (let i = 0; i < n; i++) out.push({ key: 'd' + i, bg: '#1d1d2a', bd: 'var(--gt-border-3)', ml: i === 0 ? '6px' : '0' });
  return out;
}

/**
 * What survives the local validation chain: the appid we are allowed to ask
 * Steam about, plus whatever the *curated* catalog already knows.
 *
 * `name` is EMPTY when the catalog has never seen this appid — the store page's
 * own title fills it in (`ptCheckFound`). A curated name always wins: editorial
 * fields are never written from a Steam response (ARCHITECTURE §1.4 / D2).
 */
export interface PtCheckBase {
  appid: number;
  /** curated name, or `''` when the catalog does not know this appid */
  name: string;
  dev: string;
  hue: number;
}

/**
 * The store page could not be read — an age gate, a redirect onto a different
 * app, a Steam outage, or a Worker that is down.
 *
 * P6 rule 2, and the whole point of F-2: **"we could not look" is not "we looked
 * and there is none"**. Rendering this as `widget:false` would let the add sheet
 * write `NO WIDGET ON THE STORE PAGE` onto a game that has one.
 */
export const PT_CHECK_UNAVAILABLE =
  'COULD NOT CHECK THE STORE PAGE — NOTHING IS CLAIMED EITHER WAY. TRY AGAIN.';

/**
 * `ptDoCheck` URL validation chain (prototype line 2004, INVENTORY §8.6).
 *
 * Local only, and unchanged: the three error strings are §9 copy. What is gone
 * is the fabricated success payload — `sub` used to be
 * `'SIGNUPS OPEN · PLAYTEST APP #' + (appid + 1)` on an app nobody had looked
 * at (INVENTORY §12 item 7). The verdict now comes from `ptCheckFound`, fed by
 * `GET /api/steam/ptcheck/:appid`.
 */
export function ptCheckUrl(
  url: string,
  pts: readonly Playtest[],
  gByAppid: (appid: number) => Game | null,
): { ok: false; err: string } | ({ ok: true } & PtCheckBase) {
  const m = /app\/(\d+)/.exec(url);
  if (!m || !m[1]) return { ok: false, err: 'PASTE A STEAM STORE PAGE LINK (…/APP/12345/…)' };
  const appid = Number(m[1]);
  if (pts.some((e) => e.appid === appid)) return { ok: false, err: 'ALREADY TRACKED — SEE ★ TRACKED' };
  const g = gByAppid(appid);
  if (g && !g.up) {
    return { ok: false, err: g.n.toUpperCase() + ' IS RELEASED — TEST BRANCHES FOR RELEASED GAMES ARE EXCLUDED FOR NOW' };
  }
  return { ok: true, appid, name: g ? g.n : '', dev: g ? g.dev : '—', hue: appid % 360 };
}

/**
 * A validated appid + the store page's own answer → the add-sheet result.
 *
 * `state:'open'` (instant grant) and `state:'closed'` (queued, granted in waves)
 * both mean signups are open — P6 §2. `playtestAppid` is read out of the
 * widget's `ajaxrequestplaytestaccess/<n>` POST target, so the number in
 * `PLAYTEST APP #<n>` is finally the real one.
 */
export function ptCheckFound(base: PtCheckBase, d: PtCheckData): PtCheckResult {
  const playtestAppid = d.playtestAppid && d.playtestAppid > 0 ? d.playtestAppid : 0;
  return {
    ok: true,
    appid: base.appid,
    name: base.name || d.name || 'Steam App #' + base.appid,
    dev: base.dev,
    hue: base.hue,
    widget: d.widget,
    state: d.state,
    playtestAppid,
    sub: d.widget
      ? 'SIGNUPS OPEN' + (playtestAppid ? ' · PLAYTEST APP #' + playtestAppid : '')
      : 'NO REQUEST-ACCESS WIDGET ON THE STORE PAGE',
  };
}

/**
 * The provenance a freshly tracked Steam row lands with (P6 F-3).
 *
 * `SOURCE: AUTO — STORE-WIDGET SCRAPE` is deliberately NOT one of the answers:
 * that string belongs to `playtest-scan`, which writes it on the first nightly
 * pass that confirms the widget (P6 §2 rule 3). A row created here has been
 * looked at exactly once, by the member who pasted the link, and says so.
 *
 * The no-widget branch is byte-identical to what the scan would eventually
 * write — `status:'none'` / `NO SIGNUP PAGE` /
 * `STATUS: USER-ASSERTED — NO WIDGET ON THE STORE PAGE` (INVENTORY §9, verbatim)
 * — so the row is right on day one instead of on night three.
 */
export const PT_TRACK_DATES_SRC_WIDGET =
  'STATUS: CHECKED ON ADD — REQUEST-ACCESS WIDGET ON THE STORE PAGE';
export const PT_TRACK_DATES_SRC_NO_WIDGET =
  'STATUS: USER-ASSERTED — NO WIDGET ON THE STORE PAGE';

export function ptTrackFields(input: { widget: boolean }): {
  status: 'open' | 'none';
  foot: string;
  datesSrc: string;
  note: string;
} {
  if (input.widget) {
    return {
      status: 'open',
      foot: 'SIGNUPS OPEN',
      datesSrc: PT_TRACK_DATES_SRC_WIDGET,
      note:
        'Signups open — no wave dates announced yet. Status re-checks nightly; ' +
        "opens, closes and detected waves post to What's New.",
    };
  }
  return {
    status: 'none',
    foot: 'NO SIGNUP PAGE',
    datesSrc: PT_TRACK_DATES_SRC_NO_WIDGET,
    note:
      'No request-access widget on the store page when this was added. Status ' +
      "re-checks nightly; opens, closes and detected waves post to What's New.",
  };
}

// ---------------------------------------------------------------------------
// misc small ports
// ---------------------------------------------------------------------------

/** Admin key derivation (prototype line 1888). */
export function deriveUserKey(name: string, taken: (k: string) => boolean, now: number = Date.now()): string {
  let k = name.toLowerCase().replace(/[^a-z0-9]/g, '') || 'p' + now;
  while (taken(k)) k += '2';
  return k;
}

/** Admin hue cycle (prototype line 1890). */
export const ADMIN_HUES: readonly number[] = [10, 60, 110, 170, 230, 290, 330];

export function nextHue(memberCount: number): number {
  return ADMIN_HUES[memberCount % ADMIN_HUES.length] ?? 150;
}

/** `SORT: NEWEST`-style label helper used by several count lines. */
export function gamesCount(n: number): string {
  return n + (n === 1 ? ' GAME' : ' GAMES');
}

export function matchesCount(n: number): string {
  return n + ' MATCH' + (n === 1 ? '' : 'ES');
}

/**
 * Vote sanitation on boot (prototype line 1545) — and the y/n → tier migration,
 * which is the same pass because both answer "what is this letter worth now".
 *
 * A pre-tier save holds a binary call, and somebody who already said something
 * must not lose it just because the ladder widened: `y` (▲ INTERESTED) becomes
 * `a` WANT TO PLAY, `n` (▼ NOT INTERESTED) becomes `c` PLAY IT WITHOUT ME.
 * Anything else is dropped, which reads as "no call" — the same treatment junk
 * always got.
 *
 * The map is permanent rather than a one-shot migration (docs/porting/V.md
 * decision 2): the database rewrite fixes the server's rows once, but stale
 * `qd_local_v1` dev saves and any row written by a tab still running the old
 * bundle keep arriving afterwards.
 */
export function sanitizeVotes(votes: Record<string, string>): Record<string, VoteValue> {
  const out: Record<string, VoteValue> = {};
  for (const k of Object.keys(votes)) {
    const v0 = votes[k];
    const v = v0 === 'y' ? 'a' : v0 === 'n' ? 'c' : v0;
    if (v && isCall(v)) out[k] = v;
  }
  return out;
}

/** Normalize an arbitrary call input to the row key the board uses. */
export function rowKeyOf(v: VoteValue | VoteInput | undefined): IntRowKey {
  return v && isCall(v) ? v : '';
}
